import { Tooltip, Drawer } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useState, type JSX } from 'react';
import { Text, SrOnlyText } from '../../Atoms/Text';
import { Icon, IconType } from '../../Atoms/Icon';
import {
  clearDrawerState,
  DrawerState,
  getDrawersSubject,
  removeDrawer,
  updateFromDrawerSubject
} from '../../../store/Drawer';
import { Link } from '../DetailsDrawer/detailsDrawersHelpers';
import { SalesforceLinks } from '../SalesforceLinks/SalesforceLinks';

const DEFAULT_DRAWER_CLASSES = [
  'bg-white',
  'border-l',
  'border-grey-2',
  'fixed',
  'z-50',
  'flex',
  'flex-col',
  'top-0',
  'right-0',
  'drop-shadow-xl',
  'h-screen',
  'lg:w-2/5',
  'w-full',
  'lg:max-w-[50rem]',
  'p-6',
  'overflow-auto'
].join(' ');

export const ICON_MAP_MD: { [key: string]: IconType } = {
  asset: 'assetMed',
  provisionalAsset: 'provisionalAssetMed',
  rpmAccount: 'rpmAccountMed',
  supplierMap: 'supplierMed',
  ticket: 'ticketMed'
};

export interface DrawerProps {
  id: string;
  children: JSX.Element | JSX.Element[];
  show: boolean;
  title?: string;
  subtitle?: JSX.Element;
  description?: string;
  className?: string;
  style?: object;
  link?: string;
  state?: object;
  componentName?: string;
  showButtons?: boolean;
  showIcon?: boolean;
  showExpand?: boolean;
  fullPageLink?: string;
  linkGroup?: Link[];
  showArchived?: boolean;
}

export const UpstackDrawer = ({
  id,
  show,
  title,
  subtitle,
  description,
  className,
  children,
  link = '',
  linkGroup,
  componentName,
  showButtons,
  showIcon,
  showExpand,
  fullPageLink,
  showArchived = false,
  ...props
}: DrawerProps) => {
  const drawerContentRef = useRef<HTMLDivElement | null>(null);
  const [drawerState, setDrawerState] = useState<DrawerState[]>();

  const handleClose = () => {
    clearDrawerState();
    updateFromDrawerSubject(false);
  };

  const handleBack = () => {
    removeDrawer(id);
  };

  const handleExpand = (e: React.MouseEvent<HTMLButtonElement>) => {
    const dontClose = ((e.ctrlKey || e.metaKey) && e.button === 0) || e.button === 1;
    window.open(fullPageLink, '_blank', 'noopener,noreferrer');
    if (!dontClose) handleClose();
  };

  useEffect(() => {
    const drawerStateSub = getDrawersSubject().subscribe((s) => setDrawerState(s));
    return () => {
      if (drawerStateSub) drawerStateSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (drawerContentRef.current) {
      drawerContentRef.current.scrollTop = 0;
    }
  }, [children]);

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={show}
      hideBackdrop
      ModalProps={{
        keepMounted: true,
        disablePortal: true,
        disableScrollLock: true,
        slotProps: {
          root: {
            style: { pointerEvents: 'none' }
          }
        }
      }}
      slotProps={{
        paper: {
          sx: {
            position: 'fixed',
            pointerEvents: 'auto',
            height: '100%'
          }
        }
      }}>
      <div
        ref={drawerContentRef}
        className={`${DEFAULT_DRAWER_CLASSES} ${className}`}
        {...props}>
        <div className="flex items-center justify-between">
          <Text
            dataCy="detail-drawer-title"
            className=""
            color="grey5">
            {title}
          </Text>
          <div className="flex space-x-2">
            {drawerState && drawerState?.length > 1 && (
              <button
                data-cy="full-page-button"
                className="flex  items-center justify-center border p-2 rounded-lg"
                onClick={handleBack}>
                <ArrowBackIosNewIcon
                  fontSize="small"
                  color="secondary"
                />
                <SrOnlyText dataTestId="back_page_button">back button</SrOnlyText>
              </button>
            )}
            {showExpand && fullPageLink && (
              <button
                data-cy="full-page-button"
                className="flex items-center border p-2 rounded-lg"
                onMouseUp={handleExpand}>
                <OpenInFullIcon
                  fontSize="small"
                  color="secondary"
                />
                <SrOnlyText dataTestId="full_page_button">full page</SrOnlyText>
              </button>
            )}
            <button
              data-cy="close-drawer-button"
              className="flex items-center border p-2 rounded-lg"
              data-testid="close-drawer-button"
              onClick={handleClose}>
              <CloseIcon
                fontSize="small"
                color="secondary"
              />
              <SrOnlyText>hide drawer</SrOnlyText>
            </button>
          </div>
        </div>
        <div className="flex space-x-2 items-center">
          {componentName && showIcon && (
            <Icon
              className="p-3"
              type={ICON_MAP_MD[componentName] || ''}
            />
          )}
          {subtitle && <Text className="max-w-64 truncate">{subtitle}</Text>}
          {showArchived && (
            <Text
              className="bg-grey-7 p-[1px] pl-[4.5px] pr-2 text-[10px] rounded-[4px] flex items-center !ml-3"
              weight="medium"
              color="white">
              <Icon
                type="alertWhite"
                className="p-3"
              />
              Archived
            </Text>
          )}
          {link && !linkGroup?.length && (
            <Tooltip
              arrow
              title="Open"
              placement="bottom">
              <a
                className="flex items-center"
                data-testid="openInSalesforceButton"
                href={link}
                target="_blank"
                rel="noreferrer">
                <OpenInNewIcon
                  fontSize="small"
                  color="secondary"
                />
              </a>
            </Tooltip>
          )}
          {linkGroup?.length && (
            <SalesforceLinks
              sfLink={link}
              options={linkGroup}
            />
          )}
        </div>
        <Text
          className="mb-[15px]"
          size="sm">
          {description}
        </Text>
        <div className="p-2 overflow-y-auto">{children}</div>
      </div>
    </Drawer>
  );
};
